const API_URL = import.meta.env.VITE_API_URL ?? '';

export type Rarity = 'common' | 'rare' | 'epic' | 'legendary';

export type PrizeDto = {
    code: string;
    label: string;
    icon: string;
    rarity: Rarity;
    kind: 'bsp' | 'stars' | 'gift';
    bspAmount: number | null;
    starsAmount: number | null;
};

export type OpenResult = {
    dropId: number;
    prize: PrizeDto;
    deliveryStatus: 'sent' | 'credited' | 'pending';
    attemptsLeft: number;
    bspBalance: number;
};

export type UserState = {
    user: {
        id: number;
        username?: string;
        firstName?: string;
        attempts: number;
        bspBalance: number;
        depositedTotal?: number;
        withdrawnTotal?: number;
        bonusesEarned?: number;
        refEarned?: number;
        avatar?: string;
        isAnon?: boolean;
        refCode?: string;
        role?: Role;
        battlesWon?: number;
        battlesLost?: number;
        joinedAt: string;
    };
    inventory: Array<{
        id: number;
        created_at: string;
        delivery_status: string;
        label: string;
        icon: string;
        rarity: Rarity;
        kind: string;
    }>;
};

export type FeedItem = {
    id: number;
    created_at: string;
    username?: string;
    first_name?: string;
    label: string;
    icon: string;
    rarity: Rarity;
};

export type Role = 'root' | 'admin' | 'checker' | 'user' | 'anon';

export type WithdrawMethod = 'stars' | 'ton' | 'cryptobot' | 'gift';
export type WithdrawStatus = 'pending' | 'reviewing' | 'paid' | 'rejected' | 'failed';

export type WithdrawalDto = {
    id: number;
    amount: number;
    method: WithdrawMethod;
    target: string;
    status: WithdrawStatus;
    reason: string | null;
    created_at: string;
    updated_at?: string;
};

export type AdminWithdrawalRow = {
    id: number;
    amount: number;
    method: WithdrawMethod;
    target: string;
    status: WithdrawStatus;
    reason: string | null;
    createdAt: string;
    updatedAt: string;
    user: {
        id: number;
        username?: string;
        firstName?: string;
        bspBalance: number;
        depositedTotal: number;
        withdrawnTotal?: number;
        bonusesEarned?: number;
        refEarned?: number;
        battlesWon: number;
        battlesLost: number;
        joinedAt: string;
    };
    suspicion: 'normal' | 'watch' | 'high';
};

export type AdminQueueResponse = {
    items: AdminWithdrawalRow[];
    statusCounts: Record<string, number>;
};

export type BattleParticipant = {
    userId: number;
    username?: string | null;
    firstName?: string | null;
    anon: boolean;
    isCreator: boolean;
    emoji: string;
};
export type BattleListItem = {
    id: number;
    stake: number;
    maxSlots: number;
    vip: boolean;
    pinned: boolean;
    creator: {
        id: number;
        username?: string | null;
        firstName?: string | null;
        anon?: boolean;
        emoji?: string;
    };
    joined: number;
    participants?: BattleParticipant[];
    createdAt: string;
};

export type TopCat = 'buy' | 'refs' | 'refEarn' | 'tasksDone';
export type TopRow = { rank: number; userId: number; name: string; val: string };
export type PublicSettings = {
    task_commission_pct: number;
    task_hold_price_per_hour: number;
    battle_min_stake: number;
    battle_vip_fee: number;
    fast_ticket_price: number;
    big_ticket_price: number;
    withdraw_min: number;
    piggy_stake: number;
    piggy_max_players: number;
};

export type PiggyRound = {
    id: number;
    stake: number;
    maxPlayers: number;
    joined: number;
    myPen: number | null;
};
export type PiggyRecent = {
    id: number;
    finishedAt: string;
    perWinner: number;
    pot: number;
    winnersPens: number[];
};
export type PiggyPickResult = {
    replay: boolean;
    roundId: number;
    pen: number;
    won: boolean | null;
    payout: number;
    balance: number;
    finished: boolean;
    joined?: number;
    maxPlayers?: number;
    winnerPens?: number[];
    perWinner?: number;
    pot?: number;
    houseTake?: number;
};

export type BattleHistoryItem = {
    id: number;
    stake: number;
    max_slots: number;
    status: string;
    winner_id: number | null;
    won: boolean;
    created_at: string;
    finished_at: string | null;
};

export type JoinBattleResult = {
    ok: true;
    joined: number;
    finished: boolean;
    outcome: { winnerId: number; payout: number; house: number; giveaway: number } | null;
};

export type BattleRoom = {
    id: number;
    stake: number;
    maxSlots: number;
    vip: boolean;
    status: 'open' | 'running' | 'finished' | 'cancelled' | string;
    createdAt: string;
    finishedAt: string | null;
    creator: BattleListItem['creator'];
    participants: BattleParticipant[];
    joined: number;
    winnerId: number | null;
    payout: number | null;
    pot: number;
};

/**
 * Fetch wrapper that injects the signed initData header and surfaces typed errors.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
    const initData = window.Telegram?.WebApp?.initData ?? '';
    const headers = new Headers(init.headers);
    headers.set('X-Telegram-Init-Data', initData);
    if (init.body) headers.set('Content-Type', 'application/json');

    // Dev escape hatch: if there's no initData (no Telegram WebApp around)
    // and the page exposes a ?devUserId=<id> query param OR localStorage has
    // one, send it as X-Dev-User-Id so the backend's NODE_ENV!=production
    // bypass can pick it up. Lets you preview the Mini App in a regular
    // browser tab without BotFather.
    if (!initData) {
        const devId = getDevUserId();
        if (devId) headers.set('X-Dev-User-Id', devId);
    }

    const res = await fetch(`${API_URL}${path}`, { ...init, headers });
    if (!res.ok) {
        const body = (await res.json().catch(() => ({}))) as {
            error?: string; detail?: string; pgCode?: string;
        } & Record<string, unknown>;
        throw new ApiError(res.status, body.error ?? 'unknown', path, body);
    }
    try {
        return (await res.json()) as T;
    } catch {
        // Proxy/nginx misconfiguration can return HTML with a 200 status.
        // Surface this clearly instead of letting JSON.parse throw an opaque error.
        throw new ApiError(res.status, 'invalid_response', path, {});
    }
}

function getDevUserId(): string | null {
    try {
        const qs = new URLSearchParams(window.location.search);
        const fromUrl = qs.get('devUserId');
        if (fromUrl) {
            try { localStorage.setItem('devUserId', fromUrl); } catch { /* private mode */ }
            return fromUrl;
        }
        return localStorage.getItem('devUserId');
    } catch {
        return null;
    }
}

export class ApiError extends Error {
    detail: string | undefined;
    pgCode: string | undefined;
    /** The whole error body — some errors carry data (e.g. `progress` on not_done). */
    body: Record<string, unknown> | undefined;
    constructor(
        public status: number,
        public code: string,
        public path: string,
        body?: { detail?: string; pgCode?: string } & Record<string, unknown>,
    ) {
        super(`${status} ${code} at ${path}${body?.detail ? ` — ${body.detail}` : ''}`);
        this.detail = body?.detail;
        this.pgCode = body?.pgCode;
        this.body = body;
    }
}

/** Chat gate (ОП): a guarded chat and the channels its members must subscribe to. */
export type GateChannel = { id: number; title: string; url: string; verify: boolean };
export type GateChat = {
    chatId: string; title: string; username: string | null;
    enabled: boolean; botActive: boolean; passes: number; pending: number;
    channels: GateChannel[];
};
export type GateStatus = {
    rights: { admin: boolean; canDelete: boolean; canRestrict: boolean } | null;
    channels: Array<{ id: number; botAdmin: boolean }>;
};

export const api = {
    getState: () => request<UserState>('/api/user/state'),

    openRaffle: (idempotencyKey: string) =>
        request<OpenResult>('/api/raffle/open', {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: JSON.stringify({}),
        }),

    getFeed: () => request<{ items: FeedItem[] }>('/api/raffle/feed'),

    getRole: () => request<{ role: Role }>('/api/me/role'),

    // ---- Withdrawals ----------------------------------------------------
    createWithdrawal: (
        idempotencyKey: string,
        body: { amount: number; method: WithdrawMethod; target?: string },
    ) =>
        request<{ withdrawal: WithdrawalDto; balance?: number }>('/api/withdrawals', {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: JSON.stringify(body),
        }),

    listMyWithdrawals: (status?: WithdrawStatus) => {
        const qs = status ? `?status=${encodeURIComponent(status)}` : '';
        return request<{ items: WithdrawalDto[] }>(`/api/withdrawals/me${qs}`);
    },

    withdrawalsFeed: () =>
        request<{ items: Array<{
            id: number; amount: number; method: string; name: string; createdAt: string;
        }> }>('/api/withdrawals/feed'),

    // ---- Admin ----------------------------------------------------------
    adminListWithdrawals: (status: 'all' | WithdrawStatus = 'all') =>
        request<AdminQueueResponse>(`/api/admin/withdrawals?status=${status}`),

    adminUpdateWithdrawal: (
        id: number,
        body: { status: 'reviewing' | 'paid' | 'rejected'; reason?: string },
    ) =>
        request<{ ok: true }>(`/api/admin/withdrawals/${id}`, {
            method: 'PATCH',
            body: JSON.stringify(body),
        }),

    // ---- Battles --------------------------------------------------------
    listBattles: (params: { status?: 'open' | 'history'; min?: number; max?: number; slots?: number } = {}) => {
        const sp = new URLSearchParams();
        if (params.status) sp.set('status', params.status);
        if (params.min != null) sp.set('min', String(params.min));
        if (params.max != null) sp.set('max', String(params.max));
        if (params.slots != null) sp.set('slots', String(params.slots));
        const qs = sp.toString();
        return request<{ items: BattleListItem[] | BattleHistoryItem[] }>(
            `/api/battles${qs ? `?${qs}` : ''}`,
        );
    },

    createBattle: (
        idempotencyKey: string,
        body: { stake: number; slots: number; vip?: boolean },
    ) =>
        request<{ id: number }>('/api/battles', {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: JSON.stringify(body),
        }),

    joinBattle: (id: number, idempotencyKey: string) =>
        request<JoinBattleResult>(`/api/battles/${id}/join`, {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: JSON.stringify({}),
        }),

    getBattle: (id: number) => request<BattleRoom>(`/api/battles/${id}`),

    /** Asks the backend to call Bot API `savePreparedInlineMessage` so we get
     *  an id usable by `WebApp.shareMessage(id)`. The prepared message carries
     *  an inline keyboard with a real "Join" button — `t.me/share/url` can't,
     *  it only sends raw text. */
    prepareBattleShare: (id: number) =>
        request<{ preparedMessageId: string }>(`/api/battles/${id}/prepare-share`, {
            method: 'POST',
            body: JSON.stringify({}),
        }),

    // ---- User -----------------------------------------------------------
    getMe: () => request<{
        id: number; role: Role; balance: number; freshRefsUnused: number;
        isAnon: boolean; avatar: string;
    }>('/api/me'),

    patchProfile: (body: { avatar?: string; isAnon?: boolean }) =>
        request<{ ok: true }>('/api/user/profile', {
            method: 'PATCH', body: JSON.stringify(body),
        }),

    deposit: (body: { amount: number; method: 'stars' | 'cryptobot' }) =>
        request<{ ok: true; balance: number }>('/api/user/deposit', {
            method: 'POST', body: JSON.stringify(body),
        }),

    createDepositInvoice: (body: { amount: number; method: 'stars' | 'cryptobot' }) =>
        request<{
            provider: 'stars' | 'cryptobot';
            amount: number;
            payload: string;
            link: string;
            payUrl?: string;
            amountUsd?: number;
        }>('/api/user/deposit/invoice', {
            method: 'POST', body: JSON.stringify(body),
        }),

    listNotifications: () =>
        request<{ items: Array<{
            id: number; kind: string; text: string; payload: unknown;
            seenAt: string | null; createdAt: string;
        }>; unread: number }>('/api/notifications'),

    markNotificationsRead: (body: { ids?: number[]; all?: boolean }) =>
        request<{ ok: true }>('/api/notifications/read', {
            method: 'POST', body: JSON.stringify(body),
        }),

    deleteNotifications: (body: { ids?: number[]; all?: boolean }) =>
        request<{ ok: true }>('/api/notifications/delete', {
            method: 'POST', body: JSON.stringify(body),
        }),

    // ---- Boxes ----------------------------------------------------------
    listBoxes: () => request<{ items: Array<{
        code: string; label: string; cost: number;
        payouts: Record<string, number>;
    }> }>('/api/boxes'),

    boxesFeed: () => request<{ items: Array<{
        id: number; createdAt: string; color: string; payout: number; caseCode: string;
        username: string | null; firstName: string | null;
    }> }>('/api/boxes/feed'),

    openBox: (code: string, idempotencyKey: string) =>
        request<{
            replay: boolean; color: string; payout: number;
            balance: number; poolLeft?: number;
        }>(`/api/boxes/${code}/open`, {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: JSON.stringify({}),
        }),

    // ---- FAST Giveaway --------------------------------------------------
    getFast: () => request<{
        bank: number; ticketPrice: number; housePct: number; winChancePct: number;
    }>('/api/fast'),

    fastFeed: () => request<{ items: Array<{
        id: number; createdAt: string; won: boolean; payout: number; cost: number;
        username: string | null; firstName: string | null;
    }> }>('/api/fast/feed'),

    fastBuy: (idempotencyKey: string) =>
        request<{ replay: boolean; won: boolean; payout: number; cost: number; balance: number }>(
            '/api/fast/buy', {
                method: 'POST',
                headers: { 'Idempotency-Key': idempotencyKey },
                body: JSON.stringify({}),
            }),

    fastMyHistory: () =>
        request<{ items: Array<{
            id: number; cost: number; won: boolean; payout: number; createdAt: string;
        }> }>('/api/fast/me'),

    feedWins: () =>
        request<{ items: Array<{
            kind: 'box' | 'fast' | 'big' | 'battle';
            createdAt: string; amount: number;
            username: string | null; firstName: string | null;
        }> }>('/api/feed/wins'),

    /** Public counters for the menu stat strip — real numbers, not mock. */
    siteStats: () => request<{
        users: number; online: number; freeBspGiven: number;
    }>('/api/site/stats'),

    // ---- BIG Giveaway ---------------------------------------------------
    // Tickets are referral-only now (Уведомления audio: «длинный с
    // автоучастием за рефералом»). The buy endpoint is gone; bringing a
    // referral during a round auto-mints a ticket. `refsThisRound` is the
    // number of fresh refs the user pulled in the active round so the UI
    // can show "+N билетов из рефералов".
    getBig: () => request<{
        round: { id: number; endsAt: string; pot: number; status: string };
        myTickets: number; totalTickets: number; refsThisRound: number;
    }>('/api/big'),

    bigHistory: () => request<{ items: Array<{ id: number; drawn_at: string; pot: number }> }>(
        '/api/big/history'),

    // ---- Tasks ----------------------------------------------------------
    listTasks: () => request<{ items: ServerTaskDto[]; viewer: { isPremium: boolean; appealsBanned: boolean } }>(
        '/api/tasks'),

    /** Create a task. Fast/regular for everyone (non-staff go to moderation),
     *  special for root only. */
    createTask: (idempotencyKey: string, body: CreateTaskBody) =>
        request<{ id: number; totalCost: number; status: 'pending' | 'approved' }>('/api/tasks', {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: JSON.stringify(body),
        }),

    /** channel_sub → opens an attempt; custom → reveals the instructions;
     *  special → starts the progress clock. */
    startTask: (id: number) =>
        request<{ status: string; instructions?: string | null; url?: string | null; startedAt?: string }>(
            `/api/tasks/${id}/start`, { method: 'POST', body: JSON.stringify({}) }),

    /** channel_sub → the bot checks the subscription; special → claim. */
    completeTask: (id: number, body: { proofs?: string[] } = {}) =>
        request<{ status: 'completed' | 'holding'; reward?: number; holdUntil?: string; progress?: SpecialProgress }>(
            `/api/tasks/${id}/complete`, { method: 'POST', body: JSON.stringify(body) }),

    /** Submit work on a regular task — screenshot and/or the check word. */
    submitTask: (id: number, body: { responseText?: string; screenshotBase64?: string }) =>
        request<{
            id: number; status: 'pending' | 'accepted' | 'rejected'; autoAccepted: boolean;
            reason?: 'wrong_word'; canAppeal?: boolean;
        }>(`/api/tasks/${id}/submit`, { method: 'POST', body: JSON.stringify(body) }),

    // ---- Колесо ---------------------------------------------------------
    getWheel: () => request<WheelState>('/api/wheel'),

    /** Server picks the sector; the client animates to `index` of `sectors`. */
    spinWheel: () => request<{
        index: number; sectors: WheelSector[]; prize: WheelSector; source: 'daily' | 'bonus'; state: WheelState;
    }>('/api/wheel/spin', { method: 'POST', body: JSON.stringify({}) }),

    rootWheelPrizes: () => request<{ items: RootWheelPrize[] }>('/api/root/wheel/prizes'),
    rootAddWheelPrize: (body: { kind: WheelPrizeKind; value: number; count: number }) =>
        request<{ id: number }>('/api/root/wheel/prizes', { method: 'POST', body: JSON.stringify(body) }),
    rootEditWheelPrize: (id: number, body: { value?: number; count?: number; enabled?: boolean }) =>
        request<{ ok: true }>(`/api/root/wheel/prizes/${id}`, { method: 'PATCH', body: JSON.stringify(body) }),
    rootDeleteWheelPrize: (id: number) =>
        request<{ ok: true }>(`/api/root/wheel/prizes/${id}`, { method: 'DELETE' }),

    // ---- Пересмотр ------------------------------------------------------
    /** Ask for a re-review of a rejected submission (or a manual check of a word). */
    appealSubmission: (submissionId: number, body: { comment: string; screenshotBase64?: string }) =>
        request<{ id: number; round: 1 | 2; decider: 'creator' | 'staff' }>(
            `/api/task-submissions/${submissionId}/appeal`, { method: 'POST', body: JSON.stringify(body) }),

    /** Round-1 appeals waiting for me as a task's creator. */
    incomingAppeals: () => request<{ items: TaskAppealRow[] }>('/api/task-appeals/incoming'),

    /** Staff queue: round 2 + round 1 on staff-created tasks. */
    adminListAppeals: () => request<{ items: TaskAppealRow[] }>('/api/admin/task-appeals'),

    decideAppeal: (id: number, body: { decision: 'accept' | 'reject'; comment?: string; revokeAppeals?: boolean }) =>
        request<{ ok: true; decision: string; reward?: number; appealsRevoked?: boolean }>(
            `/api/task-appeals/${id}/decide`, { method: 'POST', body: JSON.stringify(body) }),

    /** Personal submission stats: total / accepted / pending / rejected / earned. */
    myTaskStats: () =>
        request<{ total: number; accepted: number; pending: number; rejected: number; earned: number }>(
            '/api/tasks/my-stats',
        ),

    // ---- Task submission moderation (checker / admin / root) ─────────────
    adminListSubmissions: (params?: {
        status?: 'pending' | 'accepted' | 'rejected' | 'tos_violation';
        taskId?: number; limit?: number; offset?: number;
    }) => {
        const q = new URLSearchParams();
        if (params?.status)  q.set('status',  params.status);
        if (params?.taskId)  q.set('taskId',  String(params.taskId));
        if (params?.limit)   q.set('limit',   String(params.limit));
        if (params?.offset)  q.set('offset',  String(params.offset));
        return request<{
            items: TaskSubmissionRow[];
            total: number; status: string; limit: number; offset: number;
        }>(`/api/admin/task-submissions?${q.toString()}`);
    },

    adminAcceptSubmission: (id: number, comment?: string) =>
        request<{ ok: true; reward: number; multiplier: number }>(
            `/api/admin/task-submissions/${id}/accept`, {
                method: 'POST', body: JSON.stringify({ comment }),
            }),

    /** The reason is required — the executor sees it and may appeal. */
    adminRejectSubmission: (id: number, comment: string) =>
        request<{ ok: true }>(`/api/admin/task-submissions/${id}/reject`, {
            method: 'POST', body: JSON.stringify({ comment }),
        }),

    adminTosSubmission: (id: number, comment?: string) =>
        request<{ ok: true; violationsCount: number; banned: boolean }>(
            `/api/admin/task-submissions/${id}/tos`, {
                method: 'POST', body: JSON.stringify({ comment }),
            }),

    tasksTop: () => request<{ items: Array<{
        rank: number; userId: number; username?: string;
        firstName?: string; bonusesEarned: number;
    }> }>('/api/tasks/top'),

    listTops: (cat: TopCat) =>
        request<{ cat: TopCat; items: TopRow[] }>(`/api/tops?cat=${cat}`),

    tasksHolds: () =>
        request<{ count: number; sumReward: number; nearestUntil: string | null }>(
            '/api/tasks/holds'),

    publicSettings: () =>
        request<{ settings: PublicSettings; botUsername: string }>('/api/settings/public'),

    // ---- PiggyBank ------------------------------------------------------
    getPiggy: () => request<{
        round: PiggyRound | null;
        recent: PiggyRecent[];
    }>('/api/piggy'),

    piggyPick: (pen: number, idempotencyKey: string) =>
        request<PiggyPickResult>('/api/piggy/pick', {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: JSON.stringify({ pen }),
        }),

    piggyMyHistory: () =>
        request<{ items: Array<{
            roundId: number; pen: number; won: boolean | null;
            payout: number; joinedAt: string; roundStatus: string;
        }> }>('/api/piggy/me'),

    // ---- Root: forced winner --------------------------------------------
    rootForceBattleWin: (battleId: number, userId: number) =>
        request<{ ok: true }>('/api/root/battles/force-win', {
            method: 'POST', body: JSON.stringify({ battleId, userId }),
        }),

    adminListTasks: (status: 'pending' | 'approved' | 'rejected' | 'closed' = 'pending') =>
        request<{ items: AdminTaskRow[] }>(
            `/api/admin/tasks?status=${status}`),

    /** rejected needs a note; refund chooses "с возвратом" / "без возврата". */
    adminModerateTask: (id: number, body: { status: 'approved' | 'rejected' | 'closed'; note?: string; refund?: boolean }) =>
        request<{ ok: true; refunded: number; toHouse: number }>(`/api/admin/tasks/${id}`, {
            method: 'PATCH', body: JSON.stringify(body),
        }),

    adminStats: () => request<{
        deposits: number; balances: number; houseTotal: number;
        realProfit: number; withdrawnPaid: number;
        ledger: LedgerBreakdown;
        bigOpenPot: number; fastBank: number; users: number;
        battles: number; boxOpens: number; fastTickets: number; bigTickets: number;
    }>('/api/admin/stats'),

    // ---- Referrals ------------------------------------------------------
    getReferrals: () => request<{
        code: string; earned: number; freshRefsUnused: number; totalRefs: number;
        recent: Array<{ id: number; username?: string; firstName?: string; createdAt: string }>;
    }>('/api/referrals/me'),

    bindReferral: (code: string) =>
        request<{ ok: true }>('/api/referrals/bind', {
            method: 'POST', body: JSON.stringify({ code }),
        }),

    // ---- Root -----------------------------------------------------------
    rootSettings: () =>
        request<{ settings: Record<string, number | string | boolean> }>(
            '/api/root/settings'),

    rootSetSetting: (key: string, value: number | string | boolean) =>
        request<{ ok: true }>('/api/root/settings', {
            method: 'PATCH', body: JSON.stringify({ key, value }),
        }),

    rootOverview: () => request<{
        deposits: number; userBalances: number; house: number;
        bigOpenPot: number; fastBank: number; boxesStaked: number; boxesPaid: number;
        ledger: LedgerBreakdown;
    }>('/api/root/overview'),

    rootStaff: () => request<{ items: Array<{
        id: number; username?: string; first_name?: string; role: Role; created_at: string;
    }> }>('/api/root/staff'),

    rootAssignRole: (userId: number, role: Role) =>
        request<{ ok: true }>('/api/root/role', {
            method: 'POST', body: JSON.stringify({ userId, role }),
        }),

    rootGrantBalance: (userId: number, amount: number, note?: string) =>
        request<{ ok: true; balance: number }>('/api/root/grant-balance', {
            method: 'POST', body: JSON.stringify({ userId, amount, note }),
        }),

    rootBroadcast: (channel: 'notif' | 'bot' | 'both', text: string) =>
        request<{ ok: true; id: number }>('/api/root/broadcast', {
            method: 'POST', body: JSON.stringify({ channel, text }),
        }),

    rootRequestPayout: (amount: number) =>
        request<{ ok: true; id: number; ourShare: number; adminShare: number; houseTotal: number }>(
            '/api/root/payout', {
                method: 'POST', body: JSON.stringify({ amount }),
            }),

    rootListPayouts: () => request<{ items: Array<Record<string, unknown>> }>('/api/root/payouts'),

    rootClosePayout: (id: number) =>
        request<{ ok: true }>(`/api/root/payouts/${id}/close`, {
            method: 'POST', body: JSON.stringify({}),
        }),

    gateChats: () => request<{ items: GateChat[] }>('/api/gate/chats'),
    gateAddChat: (chat: string) =>
        request<{ ok: true; chatId: string; title: string }>('/api/gate/chats', {
            method: 'POST', body: JSON.stringify({ chat }),
        }),
    gateSetEnabled: (chatId: string, enabled: boolean) =>
        request<{ ok: true }>(`/api/gate/chats/${chatId}`, {
            method: 'PATCH', body: JSON.stringify({ enabled }),
        }),
    gateRemoveChat: (chatId: string) =>
        request<{ ok: true }>(`/api/gate/chats/${chatId}`, { method: 'DELETE' }),
    gateReset: (chatId: string) =>
        request<{ ok: true; cleared: number }>(`/api/gate/chats/${chatId}/reset`, {
            method: 'POST', body: JSON.stringify({}),
        }),
    gateStatus: (chatId: string) => request<GateStatus>(`/api/gate/chats/${chatId}/status`),
    gateAddChannel: (chatId: string, body: { channel: string; verify: boolean; url?: string; title?: string }) =>
        request<{ ok: true; id: number }>(`/api/gate/chats/${chatId}/channels`, {
            method: 'POST', body: JSON.stringify(body),
        }),
    gateRemoveChannel: (chatId: string, id: number) =>
        request<{ ok: true }>(`/api/gate/chats/${chatId}/channels/${id}`, { method: 'DELETE' }),

    rootBoxes: () => request<{ items: Array<{
        code: string; label: string; cost: number; enabled: boolean;
        payouts: Record<'blue'|'yellow'|'green'|'orange'|'red', number>;
        layout: Record<'blue'|'yellow'|'green'|'orange'|'red', number>;
        rtp: number; houseEdge: number;
        poolLeft: number; opens: number; paid: number;
    }> }>('/api/root/boxes'),

    rootUpdateBox: (code: string, body: {
        cost?: number;
        enabled?: boolean;
        payouts?: Record<'blue'|'yellow'|'green'|'orange'|'red', number>;
    }) =>
        request<{ ok: true }>(`/api/root/boxes/${code}`, {
            method: 'PATCH', body: JSON.stringify(body),
        }),

    rootForceBigWin: (roundId: number, userId: number, tier: '10' | '5' | '1') =>
        request<{ ok: true }>('/api/root/big/force-win', {
            method: 'POST', body: JSON.stringify({ roundId, userId, tier }),
        }),

    rootDrawBig: () => request<{ ok: true; pot: number; winners: Array<{
        tier: '10' | '5' | '1'; userId: number | null; amount: number;
    }> }>('/api/root/big/draw', {
        method: 'POST', body: JSON.stringify({}),
    }),

    rootPaymentLogs: (limit = 100) => request<{
        items: Array<{
            kind: 'deposit' | 'withdraw_paid' | 'grant';
            id: number;
            userId: number;
            actorId: number | null;
            amount: number;
            method: string | null;
            note: string | null;
            username: string | null;
            firstName: string | null;
            actorUsername: string | null;
            actorFirstName: string | null;
            createdAt: string;
        }>;
    }>(`/api/root/payment-logs?limit=${limit}`),

    /** Force-pull getStarTransactions and credit any orphaned pending deposit
     *  on the spot. Useful when a user reports "пополнил, баланс залип". */
    rootReconcileDeposits: () =>
        request<{ ok: true; scanned: number; credited: number; matchedPayloads: string[]; firstError?: string }>(
            '/api/root/deposits/reconcile',
            { method: 'POST', body: JSON.stringify({}) },
        ),

    rootPendingDeposits: () =>
        request<{
            items: Array<{
                id: number; userId: number; provider: 'stars' | 'cryptobot';
                amount: number; payload: string; chargeId: string | null;
                createdAt: string; username: string | null; firstName: string | null;
            }>;
        }>('/api/root/deposits/pending'),

    rootStarTxns: () =>
        request<{ ok: boolean; error?: string; transactions?: unknown[] }>('/api/root/deposits/star-txns'),

    rootCreditDepositManually: (depositId: number) =>
        request<{ credited: boolean; reason?: string; userId?: number; amount?: number }>(
            '/api/root/deposits/credit-manually',
            { method: 'POST', body: JSON.stringify({ depositId }) },
        ),

    // ---- Coupons ---------------------------------------------------------
    redeemCoupon: (code: string) =>
        request<{
            replay: boolean;
            kind: 'bsp' | 'deposit_pct';
            value: number;
            message: string;
        }>('/api/coupons/redeem', {
            method: 'POST',
            body: JSON.stringify({ code }),
        }),

    /** Root-only task registry (all statuses) + hard delete. */
    rootListTasks: (status: 'all' | 'pending' | 'approved' | 'rejected' | 'closed' = 'all') =>
        request<{
            items: Array<{
                id: number; creatorId: number; kind: string; title: string;
                url: string | null; reward: number; holdHours: number;
                slotsTotal: number; slotsLeft: number; status: string;
                paidAmount: number; createdAt: string;
                activeAttempts: number; doneAttempts: number;
                pendingSubmissions: number;
            }>;
        }>(`/api/root/tasks?status=${status}`),

    rootDeleteTask: (id: number) =>
        request<{
            ok: true; refunded: number; refundedToHouse: boolean;
            attemptsRemoved: number; submissionsRemoved: number;
        }>(`/api/root/tasks/${id}`, { method: 'DELETE' }),

    rootListCoupons: () => request<{
        items: Array<{
            id: number; code: string; kind: 'bsp' | 'deposit_pct'; amount: number;
            maxUses: number | null; perUserLimit: number; uses: number;
            note: string | null; createdAt: string;
            expiresAt: string | null; disabled: boolean;
        }>;
    }>('/api/root/coupons'),

    rootCreateCoupon: (body: {
        code: string; kind: 'bsp' | 'deposit_pct'; amount: number;
        maxUses?: number | null; perUserLimit?: number;
        note?: string; expiresAt?: string | null;
    }) =>
        request<{ ok: true; id: number }>('/api/root/coupons', {
            method: 'POST', body: JSON.stringify(body),
        }),

    rootToggleCoupon: (id: number) =>
        request<{ ok: true; disabled: boolean }>(`/api/root/coupons/${id}/toggle`, {
            method: 'POST', body: JSON.stringify({}),
        }),

    /** Game 667 — server-side spin. Settlement is atomic: by the time
     *  this resolves the balance is gone (or 25x'd). Animation just
     *  visualises the outcome. */
    g667Spin: (idempotencyKey: string) =>
        request<{
            replay: boolean; stake: number; won: boolean; payout: number;
            chancePct: number; balance: number;
        }>('/api/g667/spin', {
            method: 'POST',
            headers: { 'Idempotency-Key': idempotencyKey },
            body: JSON.stringify({}),
        }),

    adminUserDetails: (id: number) => request<{
        user: {
            id: string; username: string | null; first_name: string | null;
            role: string; bsp_balance: string; deposited_total: string;
            battles_won: number; battles_lost: number;
            created_at: string; last_seen_at: string;
            ref_code: string | null; referrer_id: string | null;
            ref_earned: number; fresh_refs_unused: number;
            referrer: { id: string; username: string | null; first_name: string | null } | null;
        };
        withdrawals: Array<Record<string, unknown>>;
        deposits: Array<Record<string, unknown>>;
        battles: Array<Record<string, unknown>>;
        referrals: {
            code: string | null;
            count: number;
            earnedTotal: number;
            earnedByKind: Record<string, number>;
            items: Array<{
                id: number; username: string | null; firstName: string | null;
                joinedAt: string; depositedTotal: number; balance: number;
            }>;
        };
    }>(`/api/admin/users/${id}`),

    /** Dump all real players for an off-platform giveaway. Returns the raw
     *  body (CSV / JSON / TXT-ASCII-table string) plus the row count so the
     *  UI can trigger a Blob download without re-parsing. */
    rootUsersExport: async (format: 'json' | 'csv' | 'txt'): Promise<{ body: string; count: number }> => {
        const initData = window.Telegram?.WebApp?.initData ?? '';
        const headers = new Headers();
        headers.set('X-Telegram-Init-Data', initData);
        if (!initData) {
            const devId = getDevUserId();
            if (devId) headers.set('X-Dev-User-Id', devId);
        }
        const path = `/api/root/users-export?format=${format}`;
        const res = await fetch(`${API_URL}${path}`, { headers });
        if (!res.ok) {
            const body = (await res.json().catch(() => ({}))) as { error?: string };
            throw new ApiError(res.status, body.error ?? 'unknown', path);
        }
        const text = await res.text();
        let count = 0;
        if (format === 'json') {
            try { count = (JSON.parse(text) as { count?: number }).count ?? 0; } catch { /* ignore */ }
        } else if (format === 'csv') {
            // First line is the header — subtract it from the line count.
            count = Math.max(0, text.split('\n').length - 1);
        } else {
            // TXT ends with "Всего: N" — pull the number out of the footer.
            const m = text.match(/Всего:\s*(\d+)/);
            count = m ? Number(m[1]) : 0;
        }
        return { body: text, count };
    },
};

export function newIdempotencyKey(): string {
    return crypto.randomUUID();
}

// ─── v2 Types (appended) ─────────────────────────────────────────────────────

/** Shape of one row returned by GET /api/admin/task-submissions */
export type TaskSubmissionRow = {
    id: number;
    userId: number;
    taskId: number;
    status: 'pending' | 'accepted' | 'rejected' | 'tos_violation';
    responseText: string | null;
    screenshotPath: string | null;
    moderatorComment: string | null;
    createdAt: string;
    resolvedAt: string | null;
    user: {
        id: number;
        username?: string | null;
        firstName?: string | null;
        role: string;
        registeredAt: string;
        tosViolationsCount: number;
    };
    task: {
        id: number;
        title: string;
        description: string | null;
        reward: number;
        kind: string;
        requireScreenshot: boolean;
        tosWarning: string | null;
    };
};

// ─── Tasks v4 types ──────────────────────────────────────────────────────────

export type TaskCategory = 'fast' | 'regular' | 'special';
export type TaskCheckType = 'bot' | 'auto' | 'word' | 'screenshot' | 'manual' | 'special';
export type SpecialType = 'play_game' | 'play_each_game' | 'deposit_amount' | 'daily_bonus' | 'deposit_ladder';
export type GameId = 'boxes' | 'fast' | 'g667' | 'battle' | 'piggy';

export type SpecialProgress = {
    type: SpecialType;
    started: boolean;
    startedAt: string | null;
    current: number;
    target: number;
    done: boolean;
    claimed: boolean;
    label: string;
    nextBonusPct?: number;
    depositsToday?: number;
};

export type MySubmission = {
    id: number;
    status: 'pending' | 'accepted' | 'rejected' | 'tos_violation';
    moderatorComment: string | null;
    appeals: Array<{ round: number; status: 'pending' | 'accepted' | 'rejected'; decisionComment: string | null }>;
    canAppeal: boolean;
    nextAppealRound: 1 | 2 | null;
};

export type ServerTaskDto = {
    id: number;
    kind: 'channel_sub' | 'post_view' | 'custom' | 'special';
    category: TaskCategory;
    checkType: TaskCheckType;
    title: string;
    description: string | null;
    url: string | null;
    reward: number;
    holdHours: number;
    slotsLeft: number;
    slotsTotal: number;
    createdAt: string;
    isPremiumOnly: boolean;
    requireScreenshot: boolean;
    autoCheckEnabled: boolean;
    /** false → hidden by "Скрыть недоступные" (e.g. Premium-only for a regular user). */
    available: boolean;
    unavailableReason: 'premium' | null;
    isMine: boolean;
    myStatus: string | null;
    holdUntil: string | null;
    mySubmissionStatus: string | null;
    mySubmission: MySubmission | null;
    special: { type: SpecialType; params: Record<string, unknown>; progress: SpecialProgress } | null;
    proofsNeeded: number;
    proofHint: string | null;
    rewardMultiplierForStreak: number;
    tosWarning: string | null;
    controlQuestion: string | null;
    botUsername: string | null;
};

export type CreateTaskBody =
    | {
        type: 'fast'; title: string; description?: string; url: string;
        reward: number; slots: number; holdHours: number; isPremiumOnly?: boolean;
    }
    | {
        type: 'regular'; title: string; description?: string; reward: number; slots: number;
        instructions: string; checkType: 'screenshot' | 'word'; controlWord?: string;
        url?: string; isPremiumOnly?: boolean;
    }
    | {
        type: 'special'; title: string; description?: string; specialType: SpecialType;
        params?: Record<string, unknown>; reward: number; slots?: number;
    };

export type TaskAppealRow = {
    id: number;
    round: 1 | 2;
    status: string;
    comment: string;
    screenshotPath: string | null;
    createdAt: string;
    submission: { id: number; responseText: string | null; screenshotPath: string | null; moderatorComment: string | null };
    task: {
        id: number; title: string; reward: number;
        checkType: 'word' | 'screenshot' | 'manual'; controlWord: string | null; instructions: string | null;
    };
    user: { id: number; username: string | null; firstName: string | null };
};

export type AdminTaskRow = {
    id: number;
    kind: string;
    category: TaskCategory | null;
    title: string;
    description: string | null;
    url: string | null;
    reward: number;
    holdHours: number;
    slotsTotal: number;
    slotsLeft: number;
    status: string;
    createdAt: string;
    moderationNote: string | null;
    instructions: string | null;
    checkType: TaskCheckType;
    controlWord: string | null;
    isPremiumOnly: boolean;
    /** BSP charged to the creator — what "с возвратом" gives back. */
    reserved: number;
    specialType: SpecialType | null;
    specialParams: Record<string, unknown> | null;
    creator: { id: number; username: string | null; firstName: string | null; role: string | null };
};

/** House ledger split by origin — see backend/src/services/houseLedger.ts. */
export type LedgerBreakdown = {
    games: number;
    taskFees: number;
    promos: number;
    rootTasksSpent: number;
    rootTasksReserved: number;
    net: number;
    rawTotal: number;
};

// ─── Колесо ──────────────────────────────────────────────────────────────────

export type WheelPrizeKind = 'nothing' | 'bsp' | 'deposit_bonus';
export type WheelSector = { prizeId: number; kind: WheelPrizeKind; value: number; label: string };

export type WheelState = {
    sectors: WheelSector[];
    prizes: Array<{ id: number; kind: WheelPrizeKind; value: number; count: number; label: string; chance: number }>;
    depositedThisWeek: boolean;
    dailyAvailable: boolean;
    bonusSpins: number;
    spinsAvailable: number;
    referrals: number;
    refsPerSpin: number;
    refsToNext: number;
    depositMin: number;
    bonuses: Array<{ pct: number; expiresAt: string }>;
    recent: Array<{ kind: WheelPrizeKind; value: number; label: string; source: string; createdAt: string }>;
};

export type RootWheelPrize = {
    id: number; kind: WheelPrizeKind; value: number; count: number; enabled: boolean;
    label: string; chance: number; timesWon: number;
};
